import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { ServicesBuilder } from '../utils/setup';
import type { IMenu } from '@posx/core';
import { MenuService } from '@posx/core';
import type { IMenuService } from '@posx/core';
import { Subscription } from 'dexie';
import { logLiveQueryError, isLiveQueryCursorError } from '@/utils/logger';
import { i18n } from '@/main';
import { useSectionStore } from '@/stores/section';

export const useMenuStore = defineStore('menu', {
  state: () => ({
    page: 1,
    totalPage: 0,
    menus: [] as IMenu[],
    filtersValues: {},
    menuMap: {} as { [key: string]: IMenu },
    menuService: null as IMenuService | null,
    _subscription: null as Subscription | null,
  }),
  
  getters: {
    sortedList: (state) => [...state.menus].sort((a, b) => ((a as any).sequence || 0) - ((b as any).sequence || 0))
  },

  actions: {
    // Rebuild uid→menu lookup from current menus array
    rebuildMenuMap() {
      this.menuMap = this.menus.reduce((acc, item) => { acc[item.uid] = item; return acc; }, {} as { [key: string]: IMenu });
    },

    async getService() {
      if (!this.menuService)
        // service singleton is method-only, skip deep proxy
        this.menuService = markRaw(await ServicesBuilder.getInstance().getService<IMenuService>(MenuService));
      return this.menuService;
    },

    async startLiveQuery() {
      if (this._subscription) return;
      const service = await this.getService();
      const sub: Subscription = service
        .liveQuery(() => service.table.filter(menu => menu.deleted_at === undefined || menu.deleted_at === null).toArray())
        .subscribe({
          next: (result) => {
            this.menus = result || [];
            this.rebuildMenuMap();
          },
          // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
          error: (err) => { logLiveQueryError(err, { store: 'menu' }); if (isLiveQueryCursorError(err) && this._subscription === sub) { sub.unsubscribe(); this._subscription = null; } }
        });
      this._subscription = markRaw(sub);
    },

    stopLiveQuery() {
      if (this._subscription) {
        this._subscription.unsubscribe();
        this._subscription = null;
      }
    },

    async getList() {
      const service = await this.getService();
      const result = await service.getMany(this.filtersValues, { page: this.page, pageSize: 50 });
      this.menus = result?.list || [];
      this.rebuildMenuMap();
      this.totalPage = result?.count ?? 0;
      return this.menus;
    },

    setFilterValue(values: Partial<IMenu>) {
      this.filtersValues = values;
    },

    async add(payload: Partial<IMenu>) {
      const service = await this.getService();
      const result = await service.addOne(payload as IMenu);
      await this.getList(); // Refresh list after adding
      return result;
    },

    async update(payload: Partial<IMenu>) {
      const service = await this.getService();
      const result = await service.updateOne(payload.id_in_server!, payload);
      await this.getList(); // Refresh list after updating
      return result;
    },

    async delete(model: IMenu) {
      // Block deleting a menu that a live table is still bound to
      const { list } = await (await useSectionStore().getSectionItemService()).getMany({}, { pageSize: -1 });
      const bound = list?.find(item => item.menu_uid === model.uid && !item.deleted_at);
      if (bound) throw new Error(i18n.global.t('menu.cannotDeleteBoundToTable', { table: bound.name }));
      const service = await this.getService();
      const result = await service.deleteOne(model);
      await this.getList(); // Refresh list after deleting
      return result;
    },

    async getAll() {
      const service = await this.getService();
      const result = await service.getMany(this.filtersValues, { page: 1, pageSize: 1000 });
      this.menus = result?.list || [];
      this.rebuildMenuMap();
      return this.sortedList;
    }
  },
});
